<div [class]="containerClassResolved()">
  <div class="grouped-datatable relative block w-full">
    @if (config().showSearchField) {
      <div class="relative mb-0 flex items-center justify-between gap-4">
        <div class="relative w-full max-w-[320px]">
          <app-icon
            class="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2"
            src="/icons/search.svg"
            alt="Search"
            [size]="16"
          />
          <ngx-skeleton-loader [theme]="searchSkeletonTheme" />
        </div>
      </div>
    }

    @if (config().showExpandAllTogglePlaceholder) {
      <div class="absolute right-4 top-2.5 z-10">
        <ngx-skeleton-loader class="h-5 flex" [theme]="expandAllButtonTheme" />
      </div>
    }

    <div [class]="tableSurfaceClassResolved()">
      @if (showColumnsResolved() && parentColumnsFormatted().length > 0) {
        <div
          class="grouped-skeleton-parent-header-row min-h-11 items-center border-b-2 border-shikenso-gray-300 bg-shikenso-base-white"
          [ngStyle]="parentHeaderRowStyle()"
        >
          @for (column of parentColumnsFormatted(); track column.id) {
            <div
              class="min-w-0 flex items-center"
              [ngClass]="getColumnClass(column)"
              [ngStyle]="{ padding: headerPaddingResolved() }"
            >
              <ngx-skeleton-loader
                [style.height]="headerColumnTheme.height"
                [theme]="{
                  width: column.width || headerColumnTheme.width,
                  height: headerColumnTheme.height,
                  'border-radius': headerColumnTheme['border-radius'],
                }"
              />
            </div>
          }
          <div
            class="min-h-11 min-w-0"
            [ngClass]="expandColumnClass()"
            [ngStyle]="{ padding: headerPaddingResolved() }"
          ></div>
        </div>
      }

      @for (groupIndex of groupIndices(); track groupIndex) {
        <div
          class="grouped-skeleton-parent-row h-10 min-h-10 items-center bg-shikenso-base-white"
          [class.mt-3]="$first"
          [ngStyle]="parentBodyRowStyle()"
        >
          @for (column of parentColumnsFormatted(); track column.id) {
            @let col = getCol(column, groupIndex);

            <div class="min-w-0" [ngClass]="getColumnClass(col)">
              @if (col.type === 'icon' && col.hasText) {
                <div
                  class="flex items-center"
                  [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || defaultColumnPadding }"
                >
                  <ngx-skeleton-loader
                    [style.height]="col.circleSize || iconTheme.height"
                    [theme]="{
                      width: col.circleSize || iconTheme.width,
                      height: col.circleSize || iconTheme.height,
                      'border-radius': col.iconRadius || iconTheme['border-radius'],
                    }"
                  />
                  <ngx-skeleton-loader
                    [style.height]="col.height || textWithIconTheme.height"
                    [theme]="{
                      width: col.textWidth || textWithIconTheme.width,
                      height: col.height || textWithIconTheme.height,
                      'border-radius': col.borderRadius || textWithIconTheme['border-radius'],
                    }"
                  />
                </div>
              } @else if (col.type === 'icon') {
                <div
                  class="flex items-center"
                  [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || defaultColumnPadding }"
                >
                  <ngx-skeleton-loader
                    class="w-full"
                    [style.height]="col.circleSize || iconOnlyTheme.height"
                    [theme]="{
                      width: col.circleSize || iconOnlyTheme.width,
                      height: col.circleSize || iconOnlyTheme.height,
                      'border-radius': col.borderRadius || iconOnlyTheme['border-radius'],
                    }"
                  />
                </div>
              } @else if (col.type === 'text') {
                <div
                  class="flex w-full items-center"
                  [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || defaultColumnPadding }"
                >
                  <ngx-skeleton-loader
                    class="w-full"
                    [style.height]="col.height || textTheme.height"
                    [theme]="{
                      width: col.textWidth || col.width || textTheme.width,
                      height: col.height || textTheme.height,
                      'border-radius': col.borderRadius || textTheme['border-radius'],
                    }"
                  />
                </div>
              }
            </div>
          }
          <div class="min-w-0" [ngClass]="expandColumnClass()">
            <ngx-skeleton-loader [style.height]="expandColumnTheme()['height']" [theme]="expandColumnTheme()" />
          </div>
        </div>

        <div class="grouped-skeleton-detail">
          @if (detailModeResolved() === 'expanded') {
            <div
              class="grouped-skeleton-child-wrapper pt-1"
              [class]="childWrapperClassResolved()"
              [ngStyle]="childWrapperMergedStyle()"
            >
              @if (showChildHeaderResolved() && childColumnsFormatted().length > 0) {
                <div
                  class="grouped-skeleton-child-header-row mb-2 min-h-8 items-center"
                  [ngStyle]="childBodyRowStyle()"
                >
                  @for (column of childColumnsFormatted(); track column.id) {
                    <div class="min-w-0 px-4 pb-2 pt-0" [ngClass]="getColumnClass(column)">
                      <ngx-skeleton-loader
                        [style.height]="'12px'"
                        [theme]="{
                          width: column.width || '48px',
                          height: '12px',
                          'border-radius': '4px',
                        }"
                      />
                    </div>
                  }
                </div>
              }

              @for (childRowIndex of childRowIndices(); track childRowIndex) {
                <div
                  class="grouped-skeleton-child-row mb-0 min-h-10 items-center border-b border-shikenso-gray-300"
                  [class.border-t]="$first"
                  [ngStyle]="childBodyRowStyle()"
                >
                  @for (column of childColumnsFormatted(); track column.id) {
                    @let col = getCol(column, childRowIndex);

                    <div class="min-w-0" [ngClass]="getColumnClass(col)">
                      @if (col.type === 'icon' && col.hasText) {
                        <div
                          class="flex items-center"
                          [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || '8px 16px' }"
                        >
                          <ngx-skeleton-loader
                            [style.height]="col.circleSize || iconTheme.height"
                            [theme]="{
                              width: col.circleSize || iconTheme.width,
                              height: col.circleSize || iconTheme.height,
                              'border-radius': col.iconRadius || iconTheme['border-radius'],
                            }"
                          />
                          <ngx-skeleton-loader
                            [style.height]="col.height || textWithIconTheme.height"
                            [theme]="{
                              width: col.textWidth || textWithIconTheme.width,
                              height: col.height || textWithIconTheme.height,
                              'border-radius': col.borderRadius || textWithIconTheme['border-radius'],
                            }"
                          />
                        </div>
                      } @else if (col.type === 'icon') {
                        <div
                          class="flex items-center"
                          [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || '8px 16px' }"
                        >
                          <ngx-skeleton-loader
                            class="w-full flex"
                            [style.height]="col.circleSize || iconOnlyTheme.height"
                            [style.width]="col.circleSize || iconOnlyTheme.width"
                            [theme]="{
                              width: col.circleSize || iconOnlyTheme.width,
                              height: col.circleSize || iconOnlyTheme.height,
                              'border-radius': col.borderRadius || iconOnlyTheme['border-radius'],
                            }"
                          />
                        </div>
                      } @else if (col.type === 'text') {
                        <div
                          class="flex w-full items-center"
                          [ngStyle]="{ gap: col.gap || '0.5rem', padding: col.columnPadding || '8px 16px' }"
                        >
                          <ngx-skeleton-loader
                            class="w-full"
                            [style.height]="col.height || textTheme.height"
                            [theme]="{
                              width: col.textWidth || col.width || textTheme.width,
                              height: col.height || textTheme.height,
                              'border-radius': col.borderRadius || textTheme['border-radius'],
                            }"
                          />
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }

          @if (rowSeparatorEnabledResolved() && !isLastGroup(groupIndex)) {
            <div class="grouped-skeleton-separator" role="presentation"></div>
          }
        </div>
      }
    </div>

    @if (showPaginatorResolved()) {
      <div class="skeleton-paginator flex items-center justify-end gap-4 p-4">
        <ngx-skeleton-loader class="flex" [style.height]="paginatorShortTheme.height" [theme]="paginatorShortTheme" />
        <ngx-skeleton-loader class="flex" [style.height]="paginatorLongTheme.height" [theme]="paginatorLongTheme" />
      </div>
    }
  </div>
</div>
